import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { getApiBaseUrl } from '../utils/getApiUrl';
import { canAccessComplaints, isMainAdmin } from '../utils/complaintsAccess';
import ConfirmModal from '../components/common/ConfirmModal';

type ComplaintStatus = 'submitted' | 'under_review' | 'closed';
type SubmissionType = 'complaint' | 'survey';

interface ComplaintItem {
  id: number;
  full_name: string;
  phone: string;
  description: string;
  submission_type?: SubmissionType;
  tracking_code?: string;
  status: ComplaintStatus;
  assigned_admin_username?: string;
  created_at: string;
}

interface ComplaintStats {
  open_count: number;
  total: number;
  by_status: Record<string, number>;
  by_type?: Record<string, number>;
}

const STATUS_OPTIONS: { value: ComplaintStatus | ''; label: string }[] = [
  { value: '', label: 'همه وضعیت‌ها' },
  { value: 'submitted', label: 'ثبت شده' },
  { value: 'under_review', label: 'در حال بررسی' },
  { value: 'closed', label: 'بسته شده' },
];

const TYPE_TABS: { value: SubmissionType; label: string }[] = [
  { value: 'complaint', label: 'شکایات' },
  { value: 'survey', label: 'نظرسنجی‌ها' },
];

const getStatusLabel = (status: string) => {
  if (status === 'resolved') return 'بسته شده';
  return STATUS_OPTIONS.find((o) => o.value === status)?.label || status;
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'submitted':
      return 'bg-amber-100 text-amber-800';
    case 'under_review':
      return 'bg-blue-100 text-blue-800';
    case 'closed':
    case 'resolved':
      return 'bg-gray-100 text-gray-700';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const AdminComplaints: React.FC = () => {
  const navigate = useNavigate();
  const [activeType, setActiveType] = useState<SubmissionType>('complaint');
  const [complaints, setComplaints] = useState<ComplaintItem[]>([]);
  const [stats, setStats] = useState<ComplaintStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<ComplaintStatus | ''>('');
  const [search, setSearch] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<ComplaintItem | null>(null);
  const [deleting, setDeleting] = useState(false);
  const mainAdmin = isMainAdmin();

  useEffect(() => {
    if (!canAccessComplaints()) {
      navigate('/admin/dashboard', { replace: true });
    }
  }, [navigate]);

  const fetchData = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    setLoading(true);
    setError(null);
    const apiBase = getApiBaseUrl();
    const headers = {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };

    try {
      const params = new URLSearchParams();
      params.set('submission_type', activeType);
      if (statusFilter) params.set('status', statusFilter);
      if (search.trim()) params.set('search', search.trim());

      const [listRes, statsRes, typeStatsRes] = await Promise.all([
        fetch(`${apiBase}/admin/complaints?${params}`, { headers }),
        fetch(`${apiBase}/admin/complaints/stats?submission_type=${activeType}`, { headers }),
        fetch(`${apiBase}/admin/complaints/stats`, { headers }),
      ]);

      if (listRes.status === 401) {
        navigate('/admin/login');
        return;
      }
      if (!listRes.ok) throw new Error('خطا در دریافت لیست');

      setComplaints(await listRes.json());
      if (statsRes.ok) {
        const tabStats = await statsRes.json();
        if (typeStatsRes.ok) {
          const globalStats = await typeStatsRes.json();
          tabStats.by_type = globalStats.by_type;
        }
        setStats(tabStats);
      }
    } catch (err) {
      if (err instanceof TypeError && err.message === 'Failed to fetch') {
        setError('اتصال به سرور برقرار نشد. بک‌اند را اجرا کنید (پورت ۸۰۰۰) و صفحه را رفرش کنید.');
      } else {
        setError(err instanceof Error ? err.message : 'خطای ناشناخته');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, activeType]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchData();
  };

  const confirmDelete = async () => {
    if (!deleteTarget || deleting) return;
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    setDeleting(true);
    setError(null);
    try {
      const res = await fetch(`${getApiBaseUrl()}/admin/complaints/${deleteTarget.id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (res.status === 401) {
        navigate('/admin/login');
        return;
      }
      if (!res.ok) {
        let msg = 'حذف انجام نشد';
        try {
          const errBody = await res.json();
          if (errBody.detail) {
            msg = typeof errBody.detail === 'string' ? errBody.detail : JSON.stringify(errBody.detail);
          }
        } catch {
          /* ignore */
        }
        throw new Error(msg);
      }
      setDeleteTarget(null);
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'خطا در حذف');
    } finally {
      setDeleting(false);
    }
  };

  const isComplaintTab = activeType === 'complaint';
  const emptyLabel = isComplaintTab ? 'شکایتی یافت نشد' : 'نظرسنجی‌ای یافت نشد';

  if (loading && complaints.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ConfirmModal
        isOpen={deleteTarget !== null}
        title={isComplaintTab ? 'حذف دائمی شکایت' : 'حذف دائمی نظرسنجی'}
        message={
          deleteTarget
            ? `«${deleteTarget.full_name}» (${deleteTarget.tracking_code || deleteTarget.id}) به‌طور کامل از سیستم حذف می‌شود و قابل بازگشت نیست. ادامه می‌دهید؟`
            : ''
        }
        onConfirm={() => void confirmDelete()}
        onCancel={() => {
          if (!deleting) setDeleteTarget(null);
        }}
        confirmText={deleting ? 'در حال حذف...' : 'حذف کامل'}
        cancelText="انصراف"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
      />

      <div>
        <h1 className="text-3xl font-bold text-gray-900">انتقادات و پیشنهادات</h1>
        <p className="text-gray-600 mt-1">مشاهده و پیگیری شکایات و نظرسنجی‌های ثبت‌شده</p>
      </div>

      <div className="flex flex-wrap gap-2 p-1 bg-gray-100 rounded-xl w-fit">
        {TYPE_TABS.map((tab) => (
          <button
            key={tab.value}
            type="button"
            onClick={() => {
              setActiveType(tab.value);
              setStatusFilter('');
            }}
            className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-colors ${
              activeType === tab.value
                ? 'bg-white text-blue-700 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {tab.label}
            {stats?.by_type && (
              <span className="mr-2 text-xs opacity-70">
                ({stats.by_type[tab.value] ?? '—'})
              </span>
            )}
          </button>
        ))}
      </div>

      {stats && isComplaintTab && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl shadow p-4 border-r-4 border-amber-500">
            <p className="text-sm text-gray-500">در انتظار رسیدگی</p>
            <p className="text-2xl font-bold text-gray-900">{stats.open_count}</p>
          </div>
          <div className="bg-white rounded-xl shadow p-4">
            <p className="text-sm text-gray-500">ثبت شده</p>
            <p className="text-2xl font-bold">{stats.by_status.submitted || 0}</p>
          </div>
          <div className="bg-white rounded-xl shadow p-4">
            <p className="text-sm text-gray-500">در حال بررسی</p>
            <p className="text-2xl font-bold">{stats.by_status.under_review || 0}</p>
          </div>
          <div className="bg-white rounded-xl shadow p-4 border-r-4 border-slate-400">
            <p className="text-sm text-gray-500">بسته شده</p>
            <p className="text-2xl font-bold">
              {(stats.by_status.closed || 0) + (stats.by_status.resolved || 0)}
            </p>
          </div>
        </div>
      )}

      {stats && !isComplaintTab && (
        <div className="bg-white rounded-xl shadow p-4 border-r-4 border-emerald-500 max-w-xs">
          <p className="text-sm text-gray-500">تعداد نظرسنجی‌ها</p>
          <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
        </div>
      )}

      <div className="bg-white rounded-xl shadow p-4 flex flex-col sm:flex-row gap-4">
        {isComplaintTab && (
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as ComplaintStatus | '')}
            className="px-4 py-2 border border-gray-200 rounded-lg text-sm"
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value || 'all'} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        )}
        <form onSubmit={handleSearch} className="flex flex-1 gap-2">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="جستجو: نام، موبایل، کد پیگیری..."
            className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm"
          />
          <button
            type="submit"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
          >
            جستجو
          </button>
        </form>
      </div>

      {error && <p className="text-red-600 bg-red-50 p-3 rounded-lg">{error}</p>}

      <div className="bg-white rounded-xl shadow overflow-hidden">
        {complaints.length === 0 ? (
          <p className="p-8 text-center text-gray-500">{emptyLabel}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">کد</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">نام</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">موبایل</th>
                  {isComplaintTab && (
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">وضعیت</th>
                  )}
                  {!isComplaintTab && (
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">خلاصه</th>
                  )}
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">تاریخ</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {complaints.map((c) => (
                  <tr key={c.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-mono" dir="ltr">
                      {c.tracking_code || c.id}
                    </td>
                    <td className="px-4 py-3 text-sm">{c.full_name}</td>
                    <td className="px-4 py-3 text-sm" dir="ltr">
                      {c.phone}
                    </td>
                    {isComplaintTab && (
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(c.status)}`}
                        >
                          {getStatusLabel(c.status)}
                        </span>
                      </td>
                    )}
                    {!isComplaintTab && (
                      <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">
                        {c.description}
                      </td>
                    )}
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {new Date(c.created_at).toLocaleDateString('fa-IR')}
                    </td>
                    <td className="px-4 py-3 text-left">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => navigate(`/admin/complaints/${c.id}`)}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                          {isComplaintTab ? 'پیگیری' : 'مشاهده'}
                        </button>
                        {mainAdmin && (
                          <button
                            type="button"
                            onClick={() => setDeleteTarget(c)}
                            className="text-red-600 hover:text-red-800 text-sm font-medium"
                            title="حذف دائمی"
                          >
                            حذف
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminComplaints;
